import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FiSearch } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash'; // lodash의 debounce 함수 import
import { Product } from '../types/types';
import { instance } from '../api/axiosInstance';

export default function SearchBar() {
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Product[]>([]);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const wrapperRef = useRef<HTMLDivElement>(null);

  // debounce 적용된 fetchSuggestions 함수
  const fetchSuggestions = useCallback(
    debounce(async (query: string) => {
      try {
        const response = await instance.get(`/api/products/search`, {
          params: { query },
        });
        setSuggestions(response.data);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
      }
    }, 300), // 300ms delay 설정
    []
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);

    if (query.length > 0) {
      fetchSuggestions(query);
    } else {
      setSuggestions([]);
    }
  };

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSuggestions([]);
    navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
  };

  const handleSuggestionClick = (productId: number) => {
    setSearchQuery('');
    setSuggestions([]);
    navigate(`/products/details/${productId}`);
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setSuggestions([]);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [wrapperRef]);

  return (
    <div className="relative" ref={wrapperRef}>
      <form
        className="relative flex items-center border rounded-full bg-[#fafafa]"
        onSubmit={handleSearchSubmit}
      >
        <button type="submit" className="p-2 text-MainTextColor">
          <FiSearch />
        </button>
        <input
          type="text"
          className="bg-transparent outline-none px-2 text-[13px] placeholder:text-[#8f9ca9] MainTextColor w-full font-normal"
          placeholder={t('search.placeholder')}
          value={searchQuery}
          onChange={handleSearchChange}
        />
      </form>
      {suggestions.length > 0 && (
        <ul className="absolute top-full mt-1 left-0 w-full bg-white border rounded-md shadow-lg z-10">
          {suggestions.map((product) => (
            <li
              key={product.id}
              onClick={() => handleSuggestionClick(product.id)}
              className="px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
            >
              {product.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
